// @givmo/board — the calendar's reads, each dark until the backend serves it (CBG-CAL1 slice 1d).
//
// ══ A ROUTE MISS IS "NOT SERVED", NEVER AN ERROR ════════════════════════════════════════════════
//
// Every calendar surface appears only once the backend serves its feature. While a flag is off, the
// route answers the absent-route 404 (Board's calendar operations per their gate, `board-org-routes.md`
// §C2; core's `/org/{org_ref}/integrations` under `ORG_INTEGRATIONS_ENABLED` and the console), and the
// reads here RESOLVE that to `null` — a settled answer meaning "not served", so the surface renders
// nothing and nothing retries. Any other failure stays an error. There is no web flag: the backend's
// answer is the gate.
//
//   * `useCalendarSenders` — the senders read (`ANY_CHANNEL`: lit by either calendar flag). The
//     schedule dialog's Invitations section, the meeting's "Not sent · Send invitations".
//   * `useBoardCalendarConnections` / `useBoardCalendarSettings` — `CONNECTED` only, and
//     `board_calendar:manage` for the connections: the Owner's two Board settings cards.
//   * `useMeetingInvitations` — `ANY_CHANNEL`: "Who's coming"'s calendar replies.
//   * `useMeetingEvents` — a meeting's event log. Served today (it is not calendar-gated), so the
//     History card decides for itself when it shows (`features/calendar/history-card.tsx`).
//   * `useOrgConnections` — core's list of the purpose's connections: Your settings → Calendar and the
//     Organization's Connected accounts.
//
// One key family, `boardCalendarKeys`, so one act refreshes every calendar read of its organization
// (`useRefreshCalendar`), with the meeting reads that carry the `calendar` block.

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  BoardCalendarConnectionOut,
  BoardCalendarSendersOut,
  BoardCalendarSettingsOut,
  OrgIntegrationConnectionOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { useBoardConnectionOperations, useBoardOperations } from '../board-runtime';
import { isNotServed } from '../lib/not-served';
import type { BoardConnectionOperations } from './connection-operations';
import {
  decodeMeetingEvents,
  decodeMeetingInvitations,
  type MeetingEvent,
  type MeetingInvitations,
} from '../features/meetings/calendar-decode';

/** The one purpose Board connects calendars for (core's registry, `org-integrations.md` §3). */
export const BOARD_MEETINGS_PURPOSE = 'board_meetings';

/**
 * A calendar connection as Board reads it: core's row, its address REQUIRED. Core's
 * `account_email` is optional since the store also holds accounts that have none (Social's Pages
 * and organization pages, SMM-BE14), but every `board_meetings` row carries one
 * (`ck_org_integration_connection_email_required`), so Board narrows ONCE, at its read, and every
 * reader below keeps the string it always had. A row is never dropped here: revoking access must
 * not depend on a field, so an address-less row (none can exist) would read as the empty string.
 */
export type CalendarConnection = OrgIntegrationConnectionOut & { account_email: string };

/** Core's rows as Board's: each with its address as a string. */
export function calendarConnections(
  rows: readonly OrgIntegrationConnectionOut[],
): CalendarConnection[] {
  return rows.map((row) => ({ ...row, account_email: row.account_email ?? '' }));
}

/** Run a read; the route miss resolves to `null` ("not served"), anything else stays an error. */
async function servedOrNull<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (error) {
    if (isNotServed(error)) return null;
    throw error;
  }
}

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

/** The calendar's query keys, rooted per organization so one act can refresh them all. */
export function boardCalendarKeys(orgRef: string | null) {
  const root = ['board-calendar', orgRef ?? ''] as const;
  return {
    root,
    senders: [...root, 'senders'] as const,
    connections: [...root, 'connections'] as const,
    settings: [...root, 'settings'] as const,
    orgConnections: [...root, 'org-connections'] as const,
    invitations: (meetingId: string) => [...root, 'invitations', meetingId] as const,
    events: (meetingId: string) => [...root, 'events', meetingId] as const,
  };
}

/** The senders the backend offers this officer: null while no calendar channel is served. */
export function useCalendarSenders(
  orgRef: string | null,
  enabled = true,
): UseQueryResult<BoardCalendarSendersOut | null, unknown> {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCalendarKeys(orgRef).senders,
    enabled: enabled && orgRef !== null,
    queryFn: () =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardCalendarSenders({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return data;
      }),
    retry: retryUnlessNotServed,
  });
}

/**
 * Every connection of the purpose with the upcoming meetings each organizes: the Owner's handover
 * view (`board_calendar:manage`). Null while the connected channel is not served.
 */
export function useBoardCalendarConnections(
  orgRef: string | null,
  enabled = true,
): UseQueryResult<BoardCalendarConnectionOut[] | null, unknown> {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCalendarKeys(orgRef).connections,
    enabled: enabled && orgRef !== null,
    queryFn: () =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardCalendarConnections({
          path: { org_ref: orgRef ?? '' },
          query: { 'page[limit]': 100 },
          throwOnError: true,
        });
        return extractList<BoardCalendarConnectionOut>(data, 'data');
      }),
    retry: retryUnlessNotServed,
  });
}

/** The calendar settings in force (the shipped defaults until an Owner adopts some). */
export function useBoardCalendarSettings(
  orgRef: string | null,
  enabled = true,
): UseQueryResult<BoardCalendarSettingsOut | null, unknown> {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCalendarKeys(orgRef).settings,
    enabled: enabled && orgRef !== null,
    queryFn: () =>
      servedOrNull(async () => {
        const { data } = await operations.officerGetBoardCalendarSettings({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return data;
      }),
    retry: retryUnlessNotServed,
  });
}

/** A meeting's invitations: its block and each invited seat's latest calendar reply. */
export function useMeetingInvitations(
  orgRef: string | null,
  meetingId: string,
  enabled = true,
): UseQueryResult<MeetingInvitations | null, unknown> {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCalendarKeys(orgRef).invitations(meetingId),
    enabled: enabled && orgRef !== null && meetingId.length > 0,
    queryFn: () =>
      servedOrNull(async () => {
        const { data } = await operations.officerGetBoardMeetingInvitations({
          path: { org_ref: orgRef ?? '', meeting_id: meetingId },
          throwOnError: true,
        });
        return decodeMeetingInvitations(data);
      }),
    retry: retryUnlessNotServed,
  });
}

/** A meeting's event log, in the backend's order, read tolerantly (`calendar-decode.ts`). */
export function useMeetingEvents(
  orgRef: string | null,
  meetingId: string,
  enabled = true,
): UseQueryResult<MeetingEvent[] | null, unknown> {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCalendarKeys(orgRef).events(meetingId),
    enabled: enabled && orgRef !== null && meetingId.length > 0,
    queryFn: () =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardMeetingEvents({
          path: { org_ref: orgRef ?? '', meeting_id: meetingId },
          throwOnError: true,
        });
        return decodeMeetingEvents(data);
      }),
    retry: retryUnlessNotServed,
  });
}

/**
 * Core's list of the purpose's connections, as this caller may see them: every one for
 * `board_calendar:manage` (and for the settings authority while Board is off), else one's own and the
 * board account. Null while core does not serve it.
 */
export function useOrgConnections(
  orgRef: string | null,
  enabled = true,
): UseQueryResult<CalendarConnection[] | null, unknown> {
  const connections = useBoardConnectionOperations();
  return useQuery({
    ...orgConnectionsQuery(connections, orgRef),
    enabled: enabled && orgRef !== null,
  });
}

/** Core's list as a query: one key and one read for every caller, with or without a port in hand. */
export function orgConnectionsQuery(
  connections: BoardConnectionOperations | null,
  orgRef: string | null,
) {
  return {
    queryKey: boardCalendarKeys(orgRef).orgConnections,
    queryFn: () =>
      servedOrNull(async () => {
        if (connections === null) return null;
        const { data } = await connections.listOrgIntegrations({
          path: { org_ref: orgRef ?? '' },
          query: { purpose: BOARD_MEETINGS_PURPOSE, 'page[limit]': 100 },
          throwOnError: true,
        });
        return calendarConnections(extractList<OrgIntegrationConnectionOut>(data, 'data'));
      }),
    retry: retryUnlessNotServed,
  };
}

/**
 * Refresh everything a calendar act can change for one organization: every calendar read, the
 * meetings list and each meeting it names, whose `calendar` block and history move with it. An act
 * on many meetings names them all (CBG-FIX114: a move's dialog, so the page it was opened on reads
 * its meeting again).
 */
export function useRefreshCalendar(orgRef: string | null) {
  const queryClient = useQueryClient();
  const operations = useBoardOperations();
  return async (...meetingIds: string[]) => {
    const path = { org_ref: orgRef ?? '' };
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: boardCalendarKeys(orgRef).root }),
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardMeetingsOptions({ path }).queryKey,
      }),
      ...meetingIds.map((meetingId) =>
        queryClient.invalidateQueries({
          queryKey: operations.officerGetBoardMeetingOptions({
            path: { ...path, meeting_id: meetingId },
          }).queryKey,
        }),
      ),
    ]);
  };
}
